import * as React from "react"

import { api, ensureActiveWorkspace, getToken, setAdminContact, setToken } from "@/lib/api"
import type { TokenResponse, User } from "@/lib/types"

interface AuthState {
  user: User | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signInWithGoogle: (credential: string) => Promise<void>
  signUp: (email: string, password: string, displayName?: string) => Promise<void>
  verifyRegistration: (email: string, code: string) => Promise<void>
  confirmPasswordReset: (email: string, code: string, newPassword: string) => Promise<void>
  signOut: () => void
}

const AuthContext = React.createContext<AuthState | null>(null)

/** Save the browser's zone on the account, so agents read "10:00" as the user means it. */
function syncTimezone(current: User): void {
  const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  if (!browserZone || browserZone === current.timezone) return
  api.updateMe({ timezone: browserZone }).catch(() => {
    /* best effort: runs fall back to the server's default zone */
  })
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<User | null>(null)
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    // Public config: the admin contact used by "contact admin" prompts app-wide.
    api
      .authConfig()
      .then((config) => setAdminContact(config.admin_contact))
      .catch(() => {
        /* best effort: prompts fall back to the billing page */
      })
  }, [])

  React.useEffect(() => {
    if (!getToken()) {
      setLoading(false)
      return
    }
    // A stored token may be expired or revoked; verify before trusting it.
    api
      .me()
      .then((me) => {
        setUser(me)
        syncTimezone(me)
        // Fire-and-forget: do NOT block `loading` (and therefore
        // Protected/Layout mounting) on this. Until it resolves, requests
        // simply omit X-Workspace-Id and the backend already defaults to
        // the caller's default workspace - the same one this picks - so
        // there is no correctness gap during the brief resolution window,
        // and a slow/hung /workspaces request can never wedge app load.
        void ensureActiveWorkspace()
      })
      .catch(() => setToken(null))
      .finally(() => setLoading(false))
  }, [])

  const applyToken = React.useCallback((response: TokenResponse) => {
    setToken(response.access_token)
    setUser(response.user)
    syncTimezone(response.user)
    // Fire-and-forget: a fresh sign-in also establishes a session, but
    // `loading` is already false by this point (no token existed at mount),
    // so Protected renders Layout immediately - this just starts resolution
    // as early as possible rather than blocking navigation on it. The
    // switcher's own defensive call covers the rest.
    void ensureActiveWorkspace()
  }, [])

  const signIn = React.useCallback(
    async (email: string, password: string) => {
      applyToken(await api.login(email, password))
    },
    [applyToken],
  )

  const signInWithGoogle = React.useCallback(
    async (credential: string) => {
      applyToken(await api.googleLogin(credential))
    },
    [applyToken],
  )

  const signUp = React.useCallback(
    async (email: string, password: string, displayName?: string) => {
      applyToken(await api.register(email, password, displayName))
    },
    [applyToken],
  )

  const verifyRegistration = React.useCallback(
    async (email: string, code: string) => {
      applyToken(await api.registerVerify(email, code))
    },
    [applyToken],
  )

  const confirmPasswordReset = React.useCallback(
    async (email: string, code: string, newPassword: string) => {
      applyToken(await api.passwordResetConfirm(email, code, newPassword))
    },
    [applyToken],
  )

  const signOut = React.useCallback(() => {
    setToken(null)
    setUser(null)
  }, [])

  const value = React.useMemo(
    () => ({
      user,
      loading,
      signIn,
      signInWithGoogle,
      signUp,
      verifyRegistration,
      confirmPasswordReset,
      signOut,
    }),
    [
      user,
      loading,
      signIn,
      signInWithGoogle,
      signUp,
      verifyRegistration,
      confirmPasswordReset,
      signOut,
    ],
  )
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const context = React.useContext(AuthContext)
  if (!context) throw new Error("useAuth must be used inside AuthProvider")
  return context
}
